import { Route } from 'app/enums';
import { UserEntity } from 'app/interfaces/entity';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { useCancelToken } from 'app/services/api/hooks';
import { sessionSelector } from 'app/store/session/selectors';
import isEmpty from 'lodash/isEmpty';
import { PlayerProfile } from 'modules/users/components/PlayerProfile';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { EmptyPlaceholder } from 'shared/components/EmptyPlaceholder';
import { Loader } from 'shared/components/Loader';

const PlayerViewPage: FC = () => {
  const currentUser = useSelector(sessionSelector.activeUser) as UserEntity;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMe = useMemo(() => {
    if (id === 'me') {
      return true;
    }

    return currentUser?.id === id;
  }, [currentUser?.id, id]);

  const [user, setUser] = useState<UserEntity | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const cancelToken = useCancelToken();

  const urlPrefix = useMemo(() => {
    if (isMe) {
      return currentUser?.id;
    }

    return id;
  }, [currentUser?.id, id, isMe]);

  useEffect(() => {
    if (isMe && isEmpty(currentUser)) {
      navigate(Route.Home);
    }
  }, [isMe, currentUser, navigate]);

  useEffect(() => {
    if (isMe && !isEmpty(currentUser)) {
      setUser(currentUser);
    }
  }, [currentUser, isMe]);

  useEffect(() => {
    if (urlPrefix && !isMe) {
      setIsLoaded(true);
      apiRequestCall<UserEntity>({
        params: {
          method: 'GET',
          resource: ApiResource.Users,
          cancelToken,
          urlPrefix,
        },
        handlers: {
          success: (data: UserEntity) => {
            setIsLoaded(false);
            setUser(data);
          },
          error: () => {
            setIsLoaded(false);
          },
        },
        rejectOnCancel: false,
      });
    }
  }, [urlPrefix, cancelToken, setIsLoaded, isMe]);

  if (isLoaded) {
    return <Loader />;
  }

  if (!user) {
    return <EmptyPlaceholder text="User not found." />;
  }

  return <PlayerProfile user={user} isMe={isMe} />;
};

export default PlayerViewPage;
